import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import FontAwesome from 'react-native-vector-icons/FontAwesome';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { useFocusEffect, useNavigation, useNavigationState } from '@react-navigation/native';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../context/PlayerContext';
import { getThemeColors } from '../utils/themeStyles';
import { type Palette } from '../utils/cyb';
import { usePalette } from '../hooks/usePalette';
import { orgApi, OrgCompany } from '../api/org';
import { useT } from '../i18n/useT';
import type { MessageKey } from '../i18n';
import { openInMission } from '../utils/missionPanel';
import { useUpdates } from '../context/UpdatesContext';

const EXPANDED_WIDTH = 220;
const COLLAPSED_WIDTH = 64;
const ANIMATION_DURATION = 280;

function getRankLabel(level: number): string {
  if (level >= 20) return 'ELITE AGENT';
  if (level >= 10) return 'AGENT CERTIFIE';
  if (level >= 5) return 'TECHNICIEN';
  return 'RECRUE';
}

interface NavItemConfig {
  name: string;
  labelKey: MessageKey;
  iconLib: 'ionicons' | 'fontawesome';
  icon: string;
  iconFocused?: string;
}

// ⚠️ KEYS, NOT TRANSLATED STRINGS — built at import time, before any hook has run.
const NAV_ITEMS: NavItemConfig[] = [
  { name: 'Missions',        labelKey: 'nav.missions',       iconLib: 'fontawesome', icon: 'bullseye' },
  // ⚠️ PAS DE PASTILLE SUR « Chat ». Elle affichait un `badge: 3` CODÉ EN DUR : trois non-lus
  //    annoncés à tout le monde, tout le temps, quel que soit l'état réel des conversations.
  //    Aucune source ne porte ce compteur — `UsChatSummary` n'a pas de champ de non-lus — et
  //    un nombre inventé vaut moins que pas de nombre.
  { name: 'Chat',            labelKey: 'nav.chat',           iconLib: 'ionicons',    icon: 'chatbox-outline',       iconFocused: 'chatbox' },
  { name: 'Dashboard',       labelKey: 'nav.dashboard',      iconLib: 'ionicons',    icon: 'bar-chart-outline',     iconFocused: 'bar-chart' },
  { name: 'Leaderboard',     labelKey: 'nav.leaderboard',    iconLib: 'ionicons',    icon: 'trophy-outline',        iconFocused: 'trophy' },
  { name: 'Invoices',        labelKey: 'nav.invoices',       iconLib: 'ionicons',    icon: 'document-text-outline', iconFocused: 'document-text' },
  { name: 'PaymentMethods',  labelKey: 'nav.paymentMethods', iconLib: 'ionicons',    icon: 'card-outline',          iconFocused: 'card' },
  { name: 'Account',         labelKey: 'nav.account',        iconLib: 'ionicons',    icon: 'person-outline',        iconFocused: 'person' },
];

interface SideMenuProps {
  missionDetailScreens?: string[];
}

const PLAYER_LEVEL = 7;
const PLAYER_XP = 65;
const PLAYER_XP_MAX = 100;

function PlayerCard({ expanded, labelOpacity }: { expanded: boolean; labelOpacity: Animated.Value }) {
  const { playerName, playerAvatar } = usePlayer();
  const { user } = useAuth();
  // ⚠️ CETTE CARTE ÉTAIT PEINTE EN DUR SUR LA PALETTE SOMBRE. Le nom (`#e8eaf6`), le rang et
  // le néon (`#00f0ff`) sont faits pour un fond nuit ; posés sur le fond clair du menu, ils
  // donnaient du presque-blanc sur blanc — le nom et le rôle disparaissaient.
  const { isDarkTheme } = useTheme();
  const C = usePalette();
  const pcStyles = useMemo(() => makePlayerCardStyles(C, isDarkTheme), [C, isDarkTheme]);
  const rank = getRankLabel(PLAYER_LEVEL);
  const xpPercent = Math.round((PLAYER_XP / PLAYER_XP_MAX) * 100);

  return (
    <View style={[pcStyles.container, !expanded && pcStyles.containerCollapsed]}>
      <View style={pcStyles.avatarWrap}>
        {playerAvatar ? (
          <Image source={playerAvatar} style={pcStyles.avatar} resizeMode="cover" />
        ) : (
          <View style={pcStyles.avatarFallback}>
            <Text style={pcStyles.avatarInitial}>{(playerName ?? 'J')[0].toUpperCase()}</Text>
          </View>
        )}
        <View style={pcStyles.levelBadge}>
          <Text style={pcStyles.levelText}>{PLAYER_LEVEL}</Text>
        </View>
      </View>

      {/* Bloc d'infos rendu UNIQUEMENT déplié : sinon le texte s'enroule dans la largeur
          étroite du rail et étire la capsule en hauteur. */}
      {expanded && (
        <Animated.View style={[pcStyles.info, { opacity: labelOpacity }]}>
          <Text style={pcStyles.name} numberOfLines={1}>{playerName ?? 'Joueur'}</Text>
          <Text style={pcStyles.rank} numberOfLines={1}>{rank}</Text>
          {!!user?.email && <Text style={pcStyles.email} numberOfLines={1}>{user.email}</Text>}
          <View style={pcStyles.xpRow}>
            <View style={pcStyles.xpTrack}>
              <View style={[pcStyles.xpFill, { width: `${xpPercent}%` as any }]} />
            </View>
            <Text style={pcStyles.xpLabel}>{PLAYER_XP}/{PLAYER_XP_MAX}</Text>
          </View>
        </Animated.View>
      )}
    </View>
  );
}

// La carte du joueur EN FONCTION DE LA CHARTE. Une `StyleSheet.create` de module est figée au
// chargement : ses couleurs seraient gelées à la première valeur et ne suivraient jamais le
// basculement de thème.
const makePlayerCardStyles = (C: Palette, dark: boolean) => StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    marginHorizontal: 8,
    marginBottom: 8,
    marginTop: 4,
    padding: 10,
    // En clair, un voile de cyan à 5 % sur un fond déjà clair ne dessine RIEN : la carte se
    // fond dans le menu. On lui donne une surface franche et un liseré qui la détoure.
    backgroundColor: dark ? 'rgba(0,240,255,0.05)' : C.card,
    borderWidth: 1,
    borderColor: dark ? 'rgba(0,240,255,0.2)' : C.borderCy,
    borderRadius: 10,
    gap: 8,
    overflow: 'hidden',
  },
  // Replié (rail) : capsule compacte, avatar centré, pas de bloc d'infos.
  containerCollapsed: { justifyContent: 'center', paddingHorizontal: 6, marginHorizontal: 6, gap: 0 },
  avatarWrap: { position: 'relative', flexShrink: 0 },
  avatar: { width: 40, height: 40, borderRadius: 20 },
  avatarFallback: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: dark ? 'rgba(0,240,255,0.15)' : C.cyan + '1F',
    borderWidth: 1,
    borderColor: dark ? 'rgba(0,240,255,0.4)' : C.cyan,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitial: { color: C.cyan, fontSize: 16, fontWeight: '800' },
  levelBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    backgroundColor: dark ? '#0a0e1a' : C.cyan,
    borderWidth: 1,
    borderColor: dark ? '#00f0ff' : C.card,
    borderRadius: 8,
    minWidth: 16,
    height: 16,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  levelText: { color: dark ? '#00f0ff' : '#ffffff', fontSize: 9, fontWeight: '800', fontFamily: 'monospace' },
  info: { flex: 1, overflow: 'hidden' },
  name: { color: C.textPrim, fontSize: 12, fontWeight: '700' },
  rank: { color: C.cyan, fontSize: 9, fontWeight: '700', fontFamily: 'monospace', marginTop: 1, marginBottom: 2 },
  email: { color: C.textSec, fontSize: 9.5, marginBottom: 4 },
  xpRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  xpTrack: {
    flex: 1,
    height: 4,
    backgroundColor: dark ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.12)',
    borderRadius: 2,
    overflow: 'hidden',
  },
  xpFill: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    backgroundColor: C.cyan,
    borderRadius: 2,
    shadowColor: C.cyan,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 4,
  },
  xpLabel: { color: C.textSec, fontSize: 9, fontFamily: 'monospace', flexShrink: 0 },
});

export default function SideMenu({ missionDetailScreens = [] }: SideMenuProps) {
  const t = useT();
  const navigation = useNavigation<any>();
  const { isDarkTheme, toggleTheme } = useTheme();
  const { logout, user, getAccessToken } = useAuth();
  const colors = getThemeColors(isDarkTheme);
  const [expanded, setExpanded] = useState(true);
  // CCT-348 — unread updates, per area. ⚠️ COUNTED, NOT WRITTEN: the previous badge here was a
  // literal `3` in the configuration, announced to everybody for ever. `useUpdates` answers 0
  // when it cannot count, so the badge disappears rather than inventing a number.
  const updates = useUpdates();
  const badgeFor = (name: string) =>
    (name === 'Chat' ? updates.chat : name === 'Dashboard' ? updates.board : 0);
  const [companies, setCompanies] = useState<OrgCompany[]>([]);

  // Entreprises de l'utilisateur (un onglet par entreprise dans le menu).
  const loadCompanies = useCallback(async () => {
    const token = getAccessToken();
    if (!user || !token) { setCompanies([]); return; }
    try { const o = await orgApi.myOrg(token); setCompanies(o.companies ?? []); }
    catch { /* ignore */ }
  }, [user, getAccessToken]);
  useFocusEffect(useCallback(() => { loadCompanies(); }, [loadCompanies]));

  // Navigation vers un écran de l'InnerStack (sans reset -> pas de remount = pas de flicker).
  //
  // CCT-346 — ⚠️ EXCEPT DURING A MISSION. Three destinations (conversations, board,
  // notifications) open OVER the mission screen there: navigating would cut the music (the track
  // follows the mission screen's FOCUS, CCT-146) and lose the open element, the scroll and the
  // progression. `openInMission` returns `false` outside a mission — we then navigate as before,
  // and that return value is the point: ignoring it means navigating on top of the panel one has
  // just opened.
  const goToInner = (name: string, params?: any) => {
    if (openInMission(name)) return;
    navigation.navigate('Main', { screen: name, params });
  };
  const widthAnim = useRef(new Animated.Value(EXPANDED_WIDTH)).current;
  const labelOpacity = useRef(new Animated.Value(1)).current;

  // Determine active route from the InnerStack (nested inside the 'Main' screen)
  const currentRouteName = useNavigationState(state => {
    const mainRoute = state?.routes?.find((r: any) => r.name === 'Main');
    const innerState = mainRoute?.state;
    if (innerState) {
      return innerState.routes?.[innerState.index ?? 0]?.name ?? 'Missions';
    }
    return 'Missions';
  });

  // Resolve which sidebar tab should be highlighted
  const getActiveTab = (): string => {
    if (missionDetailScreens.includes(currentRouteName)) return 'Missions';
    return currentRouteName;
  };
  const activeTab = getActiveTab();

  const toggle = () => {
    const toWidth = expanded ? COLLAPSED_WIDTH : EXPANDED_WIDTH;
    const toOpacity = expanded ? 0 : 1;
    Animated.timing(widthAnim, { toValue: toWidth, duration: ANIMATION_DURATION, useNativeDriver: false }).start();
    Animated.timing(labelOpacity, { toValue: toOpacity, duration: expanded ? 80 : ANIMATION_DURATION, useNativeDriver: true }).start();
    setExpanded(!expanded);
  };

  const renderIcon = (item: NavItemConfig, focused: boolean, color: string) => {
    if (item.iconLib === 'fontawesome') return <FontAwesome name={item.icon} size={20} color={color} />;
    const iconName = focused && item.iconFocused ? item.iconFocused : item.icon;
    return <Ionicons name={iconName} size={20} color={color} />;
  };

  const mainItems = NAV_ITEMS.filter(item => item.name !== 'Account');
  const accountItem = NAV_ITEMS.find(item => item.name === 'Account')!;

  return (
    <Animated.View style={[styles.container, { width: widthAnim, backgroundColor: colors.tabBackground, borderRightColor: colors.border, shadowColor: colors.cardShadow }]}>
      {/* Toggle button */}
      <TouchableOpacity onPress={toggle} style={[styles.toggleBtn, { borderBottomColor: colors.border }]} activeOpacity={0.7}>
        <View style={styles.iconContainer}>
          <Ionicons name={expanded ? 'chevron-back' : 'menu'} size={20} color={colors.iconSecondary} />
        </View>
      </TouchableOpacity>

      {/* Carte joueur (en haut du menu) — clic -> écran Compte */}
      <TouchableOpacity onPress={() => goToInner('Account')} activeOpacity={0.8}>
        <PlayerCard expanded={expanded} labelOpacity={labelOpacity} />
      </TouchableOpacity>

      {/* Main nav items */}
      <View style={styles.navItems}>
        {mainItems.map(item => {
          const focused = activeTab === item.name;
          const color = focused ? colors.tabActive : colors.tabInactive;
          return (
            <React.Fragment key={item.name}>
              <TouchableOpacity
                onPress={() => goToInner(item.name, item.name === 'Missions' ? { companyId: undefined, companyName: undefined } : undefined)}
                style={[styles.navItem, { backgroundColor: focused ? colors.tabActiveBackground : 'transparent' }]}
                activeOpacity={0.7}
              >
                <View style={styles.iconContainer}>
                  {renderIcon(item, focused, color)}
                  {!!badgeFor(item.name) && (
                    <View style={styles.iconBadge}>
                      {/* Beyond 99 the bubble would stop being readable — and the difference
                          between 100 and 400 unread changes nothing to what one does next. */}
                      <Text style={styles.iconBadgeText}>
                        {badgeFor(item.name) > 99 ? '99+' : badgeFor(item.name)}
                      </Text>
                    </View>
                  )}
                </View>
                <Animated.Text style={[styles.navLabel, { color, opacity: labelOpacity }]} numberOfLines={1}>
                  {t(item.labelKey)}
                </Animated.Text>
              </TouchableOpacity>

              {/* Sous « Missions » : une entrée par entreprise -> filtre les missions de cette entreprise */}
              {item.name === 'Missions' && expanded && companies.map((co) => {
                const pending = co.status === 'pending_activation';
                return (
                  <TouchableOpacity
                    key={co.companyid}
                    onPress={() => goToInner('Missions', { companyId: co.companyid, companyName: co.name })}
                    style={styles.companyItem}
                    activeOpacity={0.7}
                  >
                    <View style={[styles.companyDot, { backgroundColor: pending ? '#D97706' : colors.tabActive }]} />
                    <Animated.Text style={[styles.companyLabel, { color: colors.tabInactive, opacity: labelOpacity }]} numberOfLines={1}>
                      {co.name}
                    </Animated.Text>
                  </TouchableOpacity>
                );
              })}
            </React.Fragment>
          );
        })}

        {/* Espace entreprise (gestion admin) — entrée de menu dédiée */}
        <TouchableOpacity
          onPress={() => goToInner('CompanyAdmin')}
          style={[styles.navItem, { marginTop: 6, backgroundColor: activeTab === 'CompanyAdmin' ? colors.tabActiveBackground : 'transparent' }]}
          activeOpacity={0.7}
        >
          <View style={styles.iconContainer}>
            <Ionicons name="business-outline" size={20} color={activeTab === 'CompanyAdmin' ? colors.tabActive : colors.tabInactive} />
          </View>
          <Animated.Text style={[styles.navLabel, { color: activeTab === 'CompanyAdmin' ? colors.tabActive : colors.tabInactive, opacity: labelOpacity }]} numberOfLines={1}>
            {t('nav.company')}
          </Animated.Text>
        </TouchableOpacity>
      </View>

      {/* Bottom section */}
      <View style={styles.bottomSection}>
        {/* Account item */}
        {(() => {
          const focused = activeTab === accountItem.name;
          const color = focused ? colors.tabActive : colors.tabInactive;
          return (
            <TouchableOpacity
              onPress={() => goToInner(accountItem.name)}
              style={[styles.navItem, styles.accountItem, { backgroundColor: focused ? colors.tabActiveBackground : 'transparent', borderTopColor: colors.border }]}
              activeOpacity={0.7}
            >
              <View style={styles.iconContainer}>{renderIcon(accountItem, focused, color)}</View>
              <Animated.Text style={[styles.navLabel, { color, opacity: labelOpacity }]} numberOfLines={1}>{t(accountItem.labelKey)}</Animated.Text>
            </TouchableOpacity>
          );
        })()}

        {/* Dark/Light theme toggle */}
        <TouchableOpacity onPress={toggleTheme} style={[styles.themeToggleBtn, { borderTopColor: colors.border }]} activeOpacity={0.7}>
          <View style={styles.iconContainer}>
            <Ionicons
              name={isDarkTheme ? 'sunny-outline' : 'moon-outline'}
              size={20}
              color={colors.iconSecondary}
            />
          </View>
          <Animated.Text style={[styles.navLabel, { color: colors.iconSecondary, opacity: labelOpacity }]} numberOfLines={1}>
            {t(isDarkTheme ? 'nav.lightMode' : 'nav.darkMode')}
          </Animated.Text>
        </TouchableOpacity>

        {/* Logout */}
        <TouchableOpacity onPress={logout} style={[styles.logoutBtn, { borderTopColor: colors.border }]} activeOpacity={0.7}>
          <View style={styles.iconContainer}>
            <Ionicons name="log-out-outline" size={20} color={colors.error} />
          </View>
          <Animated.Text style={[styles.logoutLabel, { color: colors.error, opacity: labelOpacity }]} numberOfLines={1}>
            {t('nav.signOut')}
          </Animated.Text>
        </TouchableOpacity>
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  container: { height: '100%', borderRightWidth: 1, elevation: 4, shadowOffset: { width: 2, height: 0 }, shadowOpacity: 0.12, shadowRadius: 8, overflow: 'hidden', flexShrink: 0, flexDirection: 'column' },
  toggleBtn: { height: 50, paddingHorizontal: 16, flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, flexShrink: 0 },
  iconContainer: { width: 32, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  navItems: { flex: 1, paddingTop: 4, overflow: 'hidden' },
  navItem: { flexDirection: 'row', alignItems: 'center', height: 44, borderRadius: 10, marginHorizontal: 6, marginVertical: 1, paddingHorizontal: 6 },
  accountItem: { borderTopWidth: 1, borderRadius: 10, marginHorizontal: 6, paddingHorizontal: 6 },
  navLabel: { flex: 1, marginLeft: 10, fontSize: 13, fontWeight: '600' },
  companyItem: { flexDirection: 'row', alignItems: 'center', height: 34, borderRadius: 8, marginHorizontal: 6, marginLeft: 18, paddingHorizontal: 6 },
  companyDot: { width: 8, height: 8, borderRadius: 4, marginLeft: 10 },
  companyLabel: { flex: 1, marginLeft: 12, fontSize: 12, fontWeight: '500' },
  bottomSection: { flexShrink: 0 },
  themeToggleBtn: { flexDirection: 'row', alignItems: 'center', height: 44, paddingHorizontal: 6, marginHorizontal: 6, borderTopWidth: 1 },
  logoutBtn: { flexDirection: 'row', alignItems: 'center', height: 44, paddingHorizontal: 6, marginHorizontal: 6, borderTopWidth: 1, marginBottom: 8 },
  logoutLabel: { flex: 1, marginLeft: 10, fontSize: 13, fontWeight: '600' },
  iconBadge: { position: 'absolute', top: -4, right: -4, backgroundColor: '#EF4444', borderRadius: 10, minWidth: 18, height: 18, justifyContent: 'center', alignItems: 'center', paddingHorizontal: 3 },
  iconBadgeText: { color: '#FFFFFF', fontSize: 11, fontWeight: 'bold' },
});
